import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { AlarmAnimationService } from '@service/alarm-animation.service';
import { DashboardSocketService } from '@service/dashboard-view-socket.service';
import { DashboardViewService } from '@service/dashboard-view.service';
import { DeviceFlagsService } from '@service/device-flags.service';
import { NotificationService } from '@service/notification.service';
import { VesselService } from '@service/vessel.service';
import { DashboardPanelParameters } from 'ops/common/models/dashboard-panel-parameters';
import { DashboardView } from 'ops/common/models/dashboard-view';

import { Subscription, lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-ops-dashboard',
    templateUrl: './ops-dashboard.component.html',
    styleUrls: ['./ops-dashboard.component.scss'],
    providers: [AlarmAnimationService],
    standalone: false,
})
export class OpsDashboardComponent implements OnInit, OnDestroy {
    @ViewChild('confirmOverlay') confirmOverlay: ConfirmationWindowComponent;

    selectedPanelIndex: number = null;

    overlayFormStyle: any = {};

    dashboard: DashboardView;

    private hmiSubscription: Subscription;

    private _vesselName = '';

    constructor(
        private dashboardService: DashboardViewService,
        private notificationService: NotificationService,
        private deviceFlagService: DeviceFlagsService,
        private vesselService: VesselService,
        private dashboardSocketService: DashboardSocketService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit(): Promise<void> {
        await this.loadDashboard();
        this._vesselName = this.vesselService.vessel.name;
        this.dashboardSocketService.startSubscriptions();
        this.dashboardSocketService.updateListSubject().subscribe((newDashboard) => {
            this.dashboard = newDashboard;
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.hmiSubscription?.unsubscribe();
        this.dashboardSocketService.destroySubscriptions();
    }

    async loadDashboard() {
        try {
            this.dashboard = await lastValueFrom(this.dashboardService.getDashboard());
        } catch (error) {
            this.notificationService.dashboardLoadFail();
        }
    }

    stopFormEventPropogation(event: MouseEvent) {
        event.stopPropagation();
    }

    updateDashboard() {
        this.dashboardService.update(this.dashboard).subscribe({
            next: (newDashboard) => {
                this.dashboard.id = newDashboard.id;
            },
            error: (err) => {
                console.error(err);
                this.notificationService.dashboardUpdateFail();
            },
        });
    }

    select(panelIndex: number) {
        this.selectedPanelIndex = panelIndex;
    }

    clearSelection() {
        this.selectedPanelIndex = null;
    }

    handleDeleteClicked(panelIndex: number) {
        const sub = this.confirmOverlay.onOptionSelected.subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.deletePanel(panelIndex);
            }
            sub.unsubscribe();
        });

        this.confirmOverlay.setVisible(true);
    }

    deletePanel(dashboardIndex: number) {
        this.panels[dashboardIndex] = null;
        this.updateDashboard();
    }

    handleOverlayClose() {
        this.clearSelection();
    }

    handleAddPanelClicked(panelIndex: number) {
        this.openAddPanelForm(panelIndex);
    }

    openAddPanelForm(panelIndex: number) {
        this.select(panelIndex);
    }

    handleAddPanelConfirm(pannel: DashboardPanelParameters) {
        this.dashboard.panels[this.selectedPanelIndex] = pannel;
        this.selectedPanelIndex = null;
        this.updateDashboard();
    }

    shouldShowAddPanelForm() {
        return this.selectedPanelIndex !== null;
    }

    public get panels() {
        return this.dashboard.panels;
    }
    public get vesselName() {
        return this._vesselName;
    }
    public get roleName() {
        return '';
    }
}
